import { DASHBOARD, PRICING } from "@constants/internal-route.constant";
import { IAppRoutes } from "@interface/common.interface";
import ConsoleLayout from "@layout/console/ConsoleLayout";
import { setWindowTitle } from "@services/helper/common.helper";
import { TopProgressCom } from "@services/utils/topProgress.service";
import { lazy, Suspense } from "react";
import { Link, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Access, isAllowed, readAccess } from "./access";
import { AppRouteList } from "./site-msp/app.routes";

// Signed in, "/" and "/pricing" open inside the console too (App.tsx gives them to the public
// layouts only when signed out).
const Home = lazy(() => import("pages/Home"));
const Pricing = lazy(() => import("pages/pricing"));

const Notice = ({
  icon,
  title,
  text,
}: {
  icon: string;
  title: string;
  text: string;
}) => (
  <div className="app-content pt-3">
    <div className="container-fluid">
      <div className="card">
        <div className="card-body text-center py-5">
          <i className={`bi ${icon} fs-1 d-block mb-3 text-body-secondary`} />
          <h3 className="h5">{title}</h3>
          <p className="text-body-secondary mb-4">{text}</p>
          <Link to={DASHBOARD} className="btn btn-primary">
            Go to dashboard
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const Denied = () => (
  <Notice
    icon="bi-shield-lock"
    title="Permission denied"
    text="Your role does not grant access to this page."
  />
);

const NotFound = () => (
  <Notice
    icon="bi-signpost-split"
    title="Page not found"
    text="There is nothing at this address."
  />
);

const lazyPage = (Page: React.FC) => (
  <Suspense fallback={<TopProgressCom />}>
    <Page />
  </Suspense>
);

const routeList = (routes: IAppRoutes[], access: Access) => {
  return routes.map((route: IAppRoutes, index: number) => {
    const allowed = isAllowed(route, access);

    if (route.childrens && route.childrens.length) {
      return (
        <Route
          path={route.link}
          element={
            !allowed ? (
              <Denied />
            ) : route.element ? (
              lazyPage(route.element)
            ) : (
              <Outlet />
            )
          }
          key={index}
        >
          {routeList(route.childrens, access)}
        </Route>
      );
    }

    return (
      <Route
        path={route.link}
        element={
          route.redirect ? (
            <Navigate to={route.redirect} replace />
          ) : !allowed ? (
            <Denied />
          ) : (
            lazyPage(route.element)
          )
        }
        key={index}
      />
    );
  });
};

const PrivateRoutes = () => {
  setWindowTitle();
  // Read on every render (not at module load) so a client-side login is reflected immediately.
  const access = readAccess();
  return (
    <Routes>
      <Route element={<ConsoleLayout />}>
        <Route index element={lazyPage(Home)} />
        <Route path={PRICING} element={lazyPage(Pricing)} />
        {routeList(AppRouteList, access)}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export { PrivateRoutes };
